import { Archive, WarningCircle } from "@phosphor-icons/react"
import { Checkbox, FormInput, Modal, Tabs, Toast } from "@vesatogo/grass-core"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import { useEffect, useMemo, useState } from "react"

export interface Allocation {
  tradeId: number
  tradeItemId: number
  quantity: number
}

export type DistributeMode = "quantity" | "rate"
export type PaymentKind = "amount_percent" | "rate" | "flat"

interface DistributeRevisedQuantityModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (allocations: Allocation[], totalValue: number) => void
  trades: any[]
  initialAllocations?: Allocation[]
  mode?: DistributeMode
  paymentKind?: PaymentKind
  adjustmentLabel?: string
  isReduction?: boolean
  noteLabel?: string
}

interface GradeEntry {
  itemId: number
  tradeId: number
  maxQuantity: number
  rate?: number
  unit: string
  gradeName: string
  disabled: boolean
}

interface CommodityEntry {
  key: string
  varietyName: string
  commodityName?: string
  grades: GradeEntry[]
}

interface SupplierEntry {
  key: string
  name: string
  tradeIds: number[]
  commodities: CommodityEntry[]
}

const TreeCheckbox = ({
  checked,
  partial,
  disabled,
  onChange,
}: {
  checked: boolean
  partial?: boolean
  disabled?: boolean
  onChange: () => void
}) => {
  return (
    <button
      type="button"
      aria-pressed={checked || partial}
      disabled={disabled}
      onClick={onChange}
      className={classNames(
        "relative mt-[3px] inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] border transition-colors",
        checked || partial
          ? "border-transparent bg-[#799700]"
          : "border-[#999] bg-white",
        disabled && "cursor-not-allowed !bg-[#e0e0e0]"
      )}
    >
      {checked && (
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z"
            fill="#fff"
          />
        </svg>
      )}
      {partial && !checked && (
        <span className="block h-[2px] w-[10px] rounded-full bg-white" />
      )}
    </button>
  )
}

const getGradeNoteAmount = (
  grade: GradeEntry,
  adjustment: number,
  paymentKind: PaymentKind
) => {
  if (!adjustment || adjustment <= 0) return 0
  const qty = grade.maxQuantity || 0
  const rate = grade.rate ?? 0
  switch (paymentKind) {
    case "amount_percent":
      return (rate * qty * adjustment) / 100
    case "flat":
      return adjustment
    case "rate":
    default:
      return adjustment * qty
  }
}

export const DistributeRevisedQuantityModal = ({
  isOpen,
  onClose,
  onSave,
  trades,
  initialAllocations = [],
  mode = "quantity",
  paymentKind = "rate",
  adjustmentLabel = "",
  isReduction = true,
  noteLabel = "Credit",
}: DistributeRevisedQuantityModalProps) => {
  const isRate = mode === "rate"
  const [allocations, setAllocations] = useState<Record<number, string>>({})
  const [selectedItems, setSelectedItems] = useState<Record<number, boolean>>(
    {}
  )

  useEffect(() => {
    if (isOpen) {
      const newAllocs: Record<number, string> = {}
      const newSelected: Record<number, boolean> = {}
      initialAllocations.forEach(a => {
        newAllocs[a.tradeItemId] = String(a.quantity)
        if (a.quantity > 0) newSelected[a.tradeItemId] = true
      })
      setAllocations(newAllocs)
      setSelectedItems(newSelected)
    }
  }, [initialAllocations, isOpen])

  // Build Supplier -> Commodity -> Grade hierarchy
  const hierarchy = useMemo(() => {
    const supplierMap = new Map<string, SupplierEntry>()

    trades.forEach(t => {
      const sKey = `${t?.id}:${t?.seller?.id ?? t?.seller?.name ?? "unknown"}`
      let supplier = supplierMap.get(sKey)
      if (!supplier) {
        supplier = {
          key: sKey,
          name: t?.seller?.name || "--",
          tradeIds: [],
          commodities: [],
        }
        supplierMap.set(sKey, supplier)
      }
      if (t?.id != null && !supplier.tradeIds.includes(t.id)) {
        supplier.tradeIds.push(t.id)
      }

      const supplierRef = supplier
      ;(t?.trade_items || []).forEach((item: any) => {
        if (item?.id == null) return
        const variety = item.commodity_variety ?? t.commodity_variety
        const commodity = item.commodity
        const cKey = String(
          variety?.id ??
            commodity?.id ??
            variety?.name ??
            commodity?.name ??
            "commodity"
        )
        const varietyName = variety?.name || commodity?.name || "Commodity"
        const commodityName =
          commodity?.name && commodity?.name !== varietyName
            ? commodity.name
            : undefined

        let commodityEntry = supplierRef.commodities.find(c => c.key === cKey)
        if (!commodityEntry) {
          commodityEntry = { key: cKey, varietyName, commodityName, grades: [] }
          supplierRef.commodities.push(commodityEntry)
        }

        commodityEntry.grades.push({
          itemId: item.id,
          tradeId: t.id,
          maxQuantity: item.quantity,
          rate: item.rate,
          unit: item.quantity_unit?.name || "",
          gradeName: item.variety_grade?.name || "Grade",
          disabled: Boolean(t?.receipt_id),
        })
      })
    })

    return Array.from(supplierMap.values())
  }, [trades])

  const gradeById = useMemo(() => {
    const map = new Map<number, GradeEntry>()
    hierarchy.forEach(s =>
      s.commodities.forEach(c =>
        c.grades.forEach(g => {
          map.set(g.itemId, g)
        })
      )
    )
    return map
  }, [hierarchy])

  const totalAllocated = useMemo(() => {
    return Object.entries(allocations).reduce((acc, [itemIdStr, val]) => {
      const itemId = Number(itemIdStr)
      if (!selectedItems[itemId]) return acc
      const num = parseFloat(val)
      if (isNaN(num) || num <= 0) return acc

      if (!isRate) return acc + num

      const grade = gradeById.get(itemId)
      if (!grade) return acc
      return acc + getGradeNoteAmount(grade, num, paymentKind)
    }, 0)
  }, [allocations, selectedItems, isRate, gradeById, paymentKind])

  const getSupplierState = (supplier: SupplierEntry) => {
    const grades = supplier.commodities.flatMap(c => c.grades)
    const count = grades.filter(g => selectedItems[g.itemId]).length
    return {
      grades,
      count,
      total: grades.length,
      all: grades.length > 0 && count === grades.length,
      some: count > 0,
      canToggle: grades.some(g => !g.disabled),
    }
  }

  const handleToggleSupplier = (supplier: SupplierEntry) => {
    const { grades, all, canToggle } = getSupplierState(supplier)
    if (!canToggle) return
    const next = !all
    setSelectedItems(prev => {
      const updated = { ...prev }
      grades.forEach(g => {
        if (!g.disabled) updated[g.itemId] = next
      })
      return updated
    })
  }

  const handleToggleItem = (itemId: number) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId],
    }))
  }

  const gradeSuffix = (grade: GradeEntry) => {
    if (!isRate) return grade.unit
    if (paymentKind === "amount_percent") return "%"
    if (paymentKind === "flat") return "₹"
    return grade.unit
  }

  const gradeMax = (grade: GradeEntry) =>
    isRate
      ? paymentKind === "amount_percent"
        ? 100
        : Infinity
      : grade.maxQuantity

  const handleValueChange = (
    itemId: number,
    rawVal: any,
    grade: GradeEntry
  ) => {
    let val =
      typeof rawVal === "object" && rawVal?.target
        ? rawVal.target.value
        : rawVal
    const max = gradeMax(grade)
    if (parseFloat(val) < 0) val = "0"
    if (Number.isFinite(max) && parseFloat(val) > max) val = String(max)

    setAllocations(prev => ({
      ...prev,
      [itemId]: val,
    }))

    if (parseFloat(val) > 0) {
      setSelectedItems(prev => ({ ...prev, [itemId]: true }))
    }
  }

  const handleSave = () => {
    const allocs: Allocation[] = []
    hierarchy.forEach(s =>
      s.commodities.forEach(c =>
        c.grades.forEach(g => {
          if (selectedItems[g.itemId]) {
            const num = parseFloat(allocations[g.itemId] || "0")
            if (!isNaN(num) && num > 0) {
              allocs.push({
                tradeId: g.tradeId,
                tradeItemId: g.itemId,
                quantity: num,
              })
            }
          }
        })
      )
    )

    // Check if any negative or over-budget allocation (quantity only)
    if (!isRate) {
      for (const alloc of allocs) {
        const grade = hierarchy
          .flatMap(s => s.commodities)
          .flatMap(c => c.grades)
          .find(g => g.itemId === alloc.tradeItemId)
        if (grade && alloc.quantity > grade.maxQuantity) {
          return
        }
      }
    }

    onSave(allocs, totalAllocated)
  }

  const getRevisedRate = (currentRate: number, adjustment: number) => {
    if (!adjustment || Number.isNaN(adjustment)) return currentRate
    if (paymentKind === "amount_percent") {
      const delta = (currentRate * adjustment) / 100
      return isReduction ? currentRate - delta : currentRate + delta
    }
    if (paymentKind === "flat") {
      // Flat is on total value; approximate revised per-unit rate
      return currentRate
    }
    return isReduction ? currentRate - adjustment : currentRate + adjustment
  }

  const formatAdjustment = (adjustment: number, unit: string) => {
    if (!adjustment || Number.isNaN(adjustment)) {
      return paymentKind === "amount_percent"
        ? "0%"
        : paymentKind === "flat"
        ? inr(0)
        : `${inr(0)}/${unit || "kg"}`
    }
    const sign = isReduction ? "-" : "+"
    if (paymentKind === "amount_percent") return `${sign}${adjustment}%`
    if (paymentKind === "flat") return `${sign}${inr(adjustment)}`
    return `${sign}${inr(adjustment)}/${unit || "kg"}`
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={
        isRate
          ? `Distribute Revised ${adjustmentLabel}`
          : "Distribute Revised Quantity"
      }
      primaryActionButtonProps={{
        text: "Save",
        onClick: handleSave,
      }}
      secondaryActionButtonProps={{
        text: "Cancel",
        onClick: onClose,
      }}
    >
      <div className="flex flex-col h-full max-h-[70vh]">
        <div className=" overflow-y-auto flex-1 space-y-4">
          <div className="bg-blue-100 border border-blue-200 rounded-lg p-4 flex gap-3 text-blue-400">
            <Archive weight="fill" size={24} />
            <div>
              <div className="font-600">
                {isRate ? (
                  <>
                    Total {noteLabel} Note: {inr(totalAllocated)}
                  </>
                ) : (
                  <>Total Revised Quantity: {totalAllocated} Units</>
                )}
              </div>
              <div className="text-sm text-[#54647A]">
                {isRate
                  ? "You must allocate the revision across grades"
                  : "You must allocate the full quantity"}
              </div>
            </div>
          </div>

          {trades.some(t => t.receipt_id) && (
            <Toast
              title="Trades with receipt id cannot be edited."
              intent="danger"
              variant="minimal"
              className="min-w-full text-sm"
            />
          )}

          <div className="space-y-4">
            {hierarchy.map(supplier => {
              const supplierState = getSupplierState(supplier)
              return (
                <div
                  key={supplier.key}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Supplier */}
                  <div className="bg-gray-50 border-b border-gray-200 p-3 flex items-start gap-3">
                    <TreeCheckbox
                      checked={supplierState.all}
                      partial={supplierState.some && !supplierState.all}
                      disabled={!supplierState.canToggle}
                      onChange={() => handleToggleSupplier(supplier)}
                    />
                    <div className="text-sm flex-1">
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <div>
                          <span className="font-600">Supplier: </span>
                          {supplier.name}
                        </div>
                        <div>
                          <span className="font-600">Trade ID: </span>
                          {supplier.tradeIds.map(id => `#${id}`).join(", ")}
                        </div>
                        {supplier.commodities.length > 1 && (
                          <div>
                            <span className="font-600">Commodities: </span>
                            {supplier.commodities.length}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {supplierState.count} of {supplierState.total} grades
                        selected
                      </div>
                    </div>
                  </div>

                  {/* Commodities as multi tabs */}
                  <div className="p-3 border-t border-gray-200">
                    <Tabs
                      tabs={supplier.commodities.map(commodity => ({
                        id: commodity.key,
                        header: (
                          <div className="text-left">
                            <div className="text-sm font-600 leading-tight">
                              {commodity.varietyName}
                            </div>
                            {commodity.commodityName && (
                              <div className="text-xs text-gray-500">
                                {commodity.commodityName}
                              </div>
                            )}
                          </div>
                        ),
                        panel: (
                          /* Grades tree under the tab */
                          <div className="p-3 space-y-4">
                            {commodity.grades.map(grade => {
                              const isSelected = !!selectedItems[grade.itemId]
                              const val = allocations[grade.itemId] || ""
                              const adjustment = parseFloat(val) || 0
                              const currentRate = grade.rate ?? 0
                              const gradeTotal =
                                currentRate * (grade.maxQuantity || 0)
                              const noteAmount = getGradeNoteAmount(
                                grade,
                                adjustment,
                                paymentKind
                              )
                              const revisedRate = getRevisedRate(
                                currentRate,
                                adjustment
                              )
                              const unit = grade.unit || "kg"

                              return (
                                <div
                                  key={grade.itemId}
                                  className="flex items-start gap-3 pl-1"
                                >
                                  <Checkbox
                                    type="checkbox"
                                    className="mt-1"
                                    checked={isSelected}
                                    onClick={() =>
                                      handleToggleItem(grade.itemId)
                                    }
                                    onChange={() =>
                                      handleToggleItem(grade.itemId)
                                    }
                                    disabled={grade.disabled}
                                  />
                                  <div className="flex-1 space-y-2">
                                    {isRate ? (
                                      <div className="text-sm font-500 text-gray-800">
                                        {grade.gradeName} | {grade.maxQuantity}{" "}
                                        {unit} | {inr(gradeTotal)}
                                      </div>
                                    ) : (
                                      <div className="text-sm font-500 text-gray-800">
                                        {grade.gradeName} | {grade.maxQuantity}{" "}
                                        {unit}
                                      </div>
                                    )}

                                    <div className="space-y-1">
                                      <div className="flex items-center justify-between gap-2">
                                        <label className="text-sm text-gray-600">
                                          {isRate
                                            ? adjustmentLabel
                                            : "Move to Previous Patti"}{" "}
                                          <span className="text-red-500">
                                            *
                                          </span>
                                        </label>
                                        {isRate &&
                                          isSelected &&
                                          adjustment > 0 && (
                                            <div
                                              className={classNames(
                                                "text-sm font-500",
                                                isReduction
                                                  ? "text-red-500"
                                                  : "text-blue-400"
                                              )}
                                            >
                                              {noteLabel} Note:{" "}
                                              {inr(noteAmount)}
                                            </div>
                                          )}
                                      </div>
                                      <div className="relative">
                                        <FormInput
                                          type="number"
                                          disabled={!isSelected}
                                          value={val}
                                          onChange={(e: any) =>
                                            handleValueChange(
                                              grade.itemId,
                                              e,
                                              grade
                                            )
                                          }
                                          placeholder="Enter"
                                          className="w-full"
                                          leftElement={
                                            isRate &&
                                            paymentKind !== "amount_percent" ? (
                                              <div className="text-gray-500 text-sm pointer-events-none">
                                                ₹
                                              </div>
                                            ) : undefined
                                          }
                                          rightElement={
                                            <div className="text-orange-600 text-sm font-500 pointer-events-none mr-1">
                                              {gradeSuffix(grade)}
                                            </div>
                                          }
                                        />
                                      </div>
                                      {isRate ? (
                                        <table className=" mt-2 w-full text-xs text-gray-700 overflow-hidden">
                                          <thead>
                                            <tr className="">
                                              <th className="text-left px-2 py-1 font-400">
                                                Current Rate
                                              </th>
                                              <th
                                                className={classNames(
                                                  "text-left px-2 py-1 font-400",
                                                  isReduction
                                                    ? "text-red-500"
                                                    : "text-blue-400"
                                                )}
                                              >
                                                Adjustment
                                              </th>
                                              {paymentKind !== "flat" && (
                                                <th className="text-left px-2 py-1 font-400">
                                                  Revised Rate
                                                </th>
                                              )}
                                              <th className="text-left px-2 py-1 font-400">
                                                Full Quantity
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr className="!font-500 text-gray-800">
                                              <td className="px-2 py-1 font-500 text-gray-800 whitespace-nowrap">
                                                {inr(currentRate)}/{unit}
                                              </td>
                                              <td
                                                className={classNames(
                                                  "px-2 py-1 font-500 whitespace-nowrap",
                                                  isReduction
                                                    ? "text-red-500"
                                                    : "text-blue-400"
                                                )}
                                              >
                                                {formatAdjustment(
                                                  adjustment,
                                                  unit
                                                )}
                                              </td>
                                              {paymentKind !== "flat" && (
                                                <td className="px-2 py-1 whitespace-nowrap font-500 text-gray-800">
                                                  {inr(revisedRate)}/{unit}
                                                </td>
                                              )}
                                              <td className="px-2 py-1 whitespace-nowrap font-500 text-gray-800">
                                                {grade.maxQuantity} {unit}
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      ) : (
                                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                          <WarningCircle size={14} />
                                          Will be added back to Previous Patti
                                          Stock
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        ),
                      }))}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </Modal>
  )
}
